import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { DialogContext } from "src/components/elements/dialog/Dialog/DialogProvider";
import {
  mandatoryItemList,
  substituteItemList,
  substituteSuggestionItemFlag,
} from "src/components/features/cart/confirm/OrderDisclaimer/useRadioState";
import styles from "src/components/features/cart/confirm/styles.module.scss";
import { useAuthorizeDDDForDeliveryTrigger } from "src/modules/api/Order/authorizeDDDForDelivery/useAuthorizeDDDForDelivery";
import { useOrderForDeliveryWithDDDAuthenticationTrigger } from "src/modules/api/Order/orderForDeliveryWithDDDAuthentication/useOrderForDeliveryWithDDDAuthentication";
import { useOrderForDeliveryWithPaygentDddAuthenticationTrigger } from "src/modules/api/Order/orderForDeliveryWithPaygentDddAuthentication/useOrderForDeliveryWithPaygentDddAuthentication";
import { CartData } from "src/modules/domain/cart/CartData";
import { OrderSecure } from "src/modules/domain/order/OrderSecure";
import {
  AUTHENTICATION_TIMEOUT_ERROR_RESPONSE_CODE,
  PaygentDDDSecureQuery,
  PAYGENT_DDD_ERROR_RESULT,
} from "src/modules/domain/order/PaygentDDDSecureQuery";
import { PAYMENT_TYPE } from "src/modules/domain/order/payment/paymentType/paymentType";
import { useUserDeliveryAddressState } from "src/modules/domain/user/deliveryAddress/useUserDeliveryAddressState";
import { getLocalStorage, setLocalStorage } from "src/modules/localStorage";

const DDDSecureDialogContent = memo(({ src }: { src: string }) => {
  return (
    <div className={styles.dddSecureDialogBody}>
      <iframe
        srcDoc={src}
        className={styles.dddSecureDialogContent}
        title="DDDSecureDialog"
      />
    </div>
  );
});

export const useDDDSecureOrder = () => {
  const { trigger: authorizeDDDForDeliveryTrigger } =
    useAuthorizeDDDForDeliveryTrigger();
  const { trigger: orderForDeliveryWithDDDAuthentication } =
    useOrderForDeliveryWithDDDAuthenticationTrigger();
  const { trigger: triggerPaygentDDDSecureOrder } =
    useOrderForDeliveryWithPaygentDddAuthenticationTrigger();
  const { deliveryAddressState: deliveryAddress } =
    useUserDeliveryAddressState();
  const { setDialog, closeDialog } = useContext(DialogContext);
  const orderSecureRef = useRef<null | OrderSecure>(null);
  const onOrderSuccessRef = useRef<null | ((orderNumber: string) => void)>(
    null
  );

  const mandatoryList = useRecoilValue(mandatoryItemList);
  const resetMandatoryList = useResetRecoilState(mandatoryItemList);
  const substituteList = useRecoilValue(substituteItemList);
  const resetSubstituteList = useResetRecoilState(substituteItemList);
  const substituteSuggestionFlag = useRecoilValue(substituteSuggestionItemFlag);
  const resetsubstituteSuggestionFlag = useResetRecoilState(
    substituteSuggestionItemFlag
  );
  const pontaPointForUseResult = useMemo(() => {
    return getLocalStorage<number>("pontaPointForUse");
  }, []);
  const pontaPointForUse = useMemo(() => {
    return pontaPointForUseResult.isStored ? pontaPointForUseResult.value : 0;
  }, [pontaPointForUseResult]);

  const substituteListParams = useMemo(
    () =>
      substituteList.substituteItemPairs.map((pair) => ({
        cart_item_id: pair.baseCartItemId,
        substitute_shop_item_id: pair.substituteItem.shop_item_id,
      })),
    [substituteList.substituteItemPairs]
  );
  const shopItemShortageParams = useMemo(() => {
    return substituteList.substituteItemPairs.length !== 0
      ? {
          substitute_item_list: substituteListParams,
          is_selected_substitute_item_suggestion: substituteSuggestionFlag,
        }
      : mandatoryList.length !== 0
      ? {
          must_have_cart_item_id_list: mandatoryList,
          is_selected_substitute_item_suggestion: substituteSuggestionFlag,
        }
      : {};
  }, [
    substituteList.substituteItemPairs.length,
    substituteListParams,
    substituteSuggestionFlag,
    mandatoryList,
  ]);

  const DialogContent = useCallback((src: string) => {
    return <DDDSecureDialogContent src={src} />;
  }, []);

  const orderByDDDAuthentication = useCallback(async () => {
    if (!orderSecureRef.current) {
      return;
    }
    const res = await orderForDeliveryWithDDDAuthentication({
      params: {
        veritrans_credit_id: orderSecureRef.current.veritrans_credit_id,
        used_reason: orderSecureRef.current.used_reason,
        user_delivery_address_id:
          orderSecureRef.current.user_delivery_address_id,
        deluxe_request: orderSecureRef.current.deluxe_request,
        payment_type: orderSecureRef.current.payment_type,
        is_allowed_out_of_stock: orderSecureRef.current.is_allowed_out_of_stock,
        is_selected_substitute_item_suggestion: substituteSuggestionFlag,
        ...shopItemShortageParams,
      },
    });
    if (res && onOrderSuccessRef.current) {
      onOrderSuccessRef.current(res.order_no);
      resetMandatoryList();
      resetSubstituteList();
      resetsubstituteSuggestionFlag();
    }
  }, [
    orderForDeliveryWithDDDAuthentication,
    resetMandatoryList,
    resetSubstituteList,
    resetsubstituteSuggestionFlag,
    shopItemShortageParams,
    substituteSuggestionFlag,
  ]);

  const orderByPaygentDDDAuthentication = useCallback(
    async (paygentDDDSecureQuery: PaygentDDDSecureQuery) => {
      if (!orderSecureRef.current) {
        return;
      }
      const params = paygentDDDSecureQuery
        ? {
            veritrans_credit_id: orderSecureRef.current.veritrans_credit_id,
            ddd_result: paygentDDDSecureQuery.ddd_result,
            ddd_auth_id: paygentDDDSecureQuery.ddd_auth_id,
            attempt_kbn: paygentDDDSecureQuery.attempt_kbn,
            hash: paygentDDDSecureQuery.hash,
            user_delivery_address_id: Number(
              orderSecureRef.current.user_delivery_address_id
            ),
            deluxe_request: orderSecureRef.current.deluxe_request,
            ddd_response_code: paygentDDDSecureQuery.ddd_response_code,
            ddd_response_detail: paygentDDDSecureQuery.ddd_response_detail,
            ddd_requestor_error_code:
              paygentDDDSecureQuery.ddd_requestor_error_code,
            ddd_server_error_code:
              paygentDDDSecureQuery.ddd_requestor_error_code,
          }
        : {
            veritrans_credit_id: orderSecureRef.current.veritrans_credit_id,
            user_delivery_address_id: Number(
              orderSecureRef.current.user_delivery_address_id
            ),
            deluxe_request: orderSecureRef.current.deluxe_request,
          };
      const res = await triggerPaygentDDDSecureOrder({ params });
      if (res && onOrderSuccessRef.current) {
        onOrderSuccessRef.current(res.order_no);
      }
    },
    [triggerPaygentDDDSecureOrder]
  );

  const orderByDDDSecure = useCallback(
    async (
      cartData: CartData,
      obtain_ponta_point: number,
      obtain_smart_pass_ponta_point: number,
      isOutOfStockAllowed: boolean,
      onOrderSuccess: (orderNumber: string) => void
    ) => {
      const result = await authorizeDDDForDeliveryTrigger({
        params: {
          cart_id: cartData.cart_id,
          total_price: cartData.user_payment_price - pontaPointForUse,
          payment_type: PAYMENT_TYPE.CREDIT,
          user_delivery_address_id: Number(
            deliveryAddress.user_delivery_address_id
          ),
          use_ponta_point: pontaPointForUse || 0,
          obtain_ponta_point,
          obtain_smart_pass_ponta_point,
        },
      });
      if (!result) {
        orderSecureRef.current = null;
        onOrderSuccessRef.current = null;
        return;
      } else {
        setLocalStorage("pontaPointForUse", "0");
      }

      const orderSecure = OrderSecure.create({
        user_delivery_address_id: deliveryAddress.user_delivery_address_id,
        veritrans_credit_id: result.veritrans_credit_id,
        is_allowed_out_of_stock: isOutOfStockAllowed,
        total_price: cartData.user_payment_price,
        html: result.content,
        is_target_ddd_authentication: result.is_target_ddd_authentication,
        is_paygent: result.is_paygent,
      });
      orderSecureRef.current = orderSecure;
      onOrderSuccessRef.current = onOrderSuccess;
      if (result.is_target_ddd_authentication) {
        setDialog({
          content: DialogContent(orderSecure.html),
          canClose: false,
        });
        return;
      }
      orderByDDDAuthentication();
    },
    [
      DialogContent,
      authorizeDDDForDeliveryTrigger,
      deliveryAddress.user_delivery_address_id,
      orderByDDDAuthentication,
      pontaPointForUse,
      setDialog,
    ]
  );

  useEffect(() => {
    const closeDialogAndOrderByDDDAuthentication = async (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      e: MessageEvent<any>
    ) => {
      if (e.origin !== `https://${process.env.NEXT_PUBLIC_HOSTNAME}`) {
        return;
      }
      const { type, ...payload } = JSON.parse(e.data as string);
      if (type === "finishPaygentDDDSecure") {
        const paygentDDDSecureQuery = PaygentDDDSecureQuery.create(payload);
        orderByPaygentDDDAuthentication(paygentDDDSecureQuery);
        closeDialog();
        return;
      } else if (orderSecureRef.current && orderSecureRef.current.is_paygent) {
        const paygentDDDSecureQuery = PaygentDDDSecureQuery.create({
          ddd_result: PAYGENT_DDD_ERROR_RESULT,
          ddd_response_code: AUTHENTICATION_TIMEOUT_ERROR_RESPONSE_CODE,
        });
        orderByPaygentDDDAuthentication(paygentDDDSecureQuery);
        closeDialog();
      } else {
        orderByDDDAuthentication();
        closeDialog();
      }
    };
    window.addEventListener("message", closeDialogAndOrderByDDDAuthentication);
    return () =>
      window.removeEventListener(
        "message",
        closeDialogAndOrderByDDDAuthentication
      );
  }, [closeDialog, orderByDDDAuthentication, orderByPaygentDDDAuthentication]);

  return { orderByDDDSecure };
};
